import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ShopMap from '../components/ShopMap';
import pinPrompt from '../utils/pinPrompt';
import { useFeatures } from '../utils/features';
import { loadBeacons, currentActor, lastSeenLabel } from '../utils/beacons';
import { shopClock } from '../utils/shopDate';
import {
  CALIB_POLL_FOR_MS, CALIB_POLL_MS, addCalibrationPoint, calibrationAwaiting, calibrationRow, hasPoint, heardNotPlaced,
  loadCalibration, loadShopMap, parseGeometryText, patchCalibrationPoint, payloadHasChanges, refitShopModel, residualRows,
  saveShopMap, settingsWithZoneRule, shopMapPayload, snapFt, ZONE_RULES, zoneRuleOf,
} from '../utils/shopMap';

// /shop-map: the shop map the pallet-beacon locations are worked out on (jobq-api GET / PUT
// /shop_map, plan proud-dazzling-fern-jobq-design.md section E). Drag anchors and gateways (or type
// their x / y / z), place anchors a gateway heard that have no place yet, draw and rename zones, edit
// the geometry document, see the radio model and refit it, check coverage, see every pallet, and run
// the calibration walk. ONE save for the whole layout: Admin PIN (never a face) -> PUT with the rev
// it was read at; a 409 means someone else saved first.

const styles = {
  page: { maxWidth: 1400 },
  head: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 12 },
  toolbar: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, margin: '8px 0' },
  num: { width: 78 },
  smallNum: { width: 64 },
  mono: { fontFamily: 'Menlo, Consolas, monospace' },
  textarea: { fontFamily: 'Menlo, Consolas, monospace', fontSize: '0.75rem', minHeight: 220 },
  legend: { fontSize: '0.75rem', color: 'var(--bs-secondary-color, #6c757d)' },
  flagged: { color: 'var(--bs-danger, #dc3545)', fontWeight: 700 },
  modeNote: { fontSize: '0.85rem' },
  tableWrap: { maxHeight: 340, overflowY: 'auto' },
  zoneSel: { minWidth: 120, maxWidth: 170 },
};

const toNum = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const fmt = (v, d = 1) => (v === null || v === undefined || !Number.isFinite(Number(v)) ? '—' : Number(v).toFixed(d));
const shown = (v) => (v === null || v === undefined ? '' : String(v));

/** The page's working copy of a GET /shop_map answer. */
function draftFrom(body) {
  return {
    name: body.map.name,
    geometryText: JSON.stringify(body.map.geometry, null, 2),
    anchors: (body.anchors || []).map((a) => ({ ...a })),
    gateways: (body.gateways || []).map((g) => ({ ...g })),
    zones: (body.zones || []).map((z, i) => ({ ...z, _key: `z${z.zone_id || i}` })),
    zoneRule: zoneRuleOf(body.map.settings),
  };
}

function NumCell({ value, onChange, label, style = styles.num }) {
  return (
    <input type="number" step="0.5" className="form-control form-control-sm" style={style} aria-label={label}
      value={shown(value)} onChange={(e) => onChange(toNum(e.target.value))} />
  );
}

export default function ShopMapPage() {
  const { palletBeacons, loaded } = useFeatures();
  const enabled = palletBeacons.location;
  const [body, setBody] = useState(null);          // the last GET /shop_map
  const [draft, setDraft] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [msg, setMsg] = useState(null);            // {tone, text, reload?}
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState(null);          // {kind:'place', anchorNo} | {kind:'draw'} | {kind:'calib'}
  const [drawing, setDrawing] = useState({ kind: 'pallet', name: '', pts: [], x: '', y: '' });
  const [selectedZone, setSelectedZone] = useState(null);
  const [showCoverage, setShowCoverage] = useState(false);
  const [showPallets, setShowPallets] = useState(false);
  const [pallets, setPallets] = useState([]);
  const [calib, setCalib] = useState(null);        // GET /shop_map/calibration
  const [calibReads, setCalibReads] = useState(0); // every calibration read, ok or not (drives the re-read)
  const calibPollFrom = useRef(null);              // when the page started waiting for a point's first scan
  const mounted = useRef(true);
  const [calibForm, setCalibForm] = useState({ beacon_id: '', x: '', y: '', z: '3', notes: '' });

  const load = useCallback(async ({ coverage = false } = {}) => {
    const r = await loadShopMap(null, { diagnostics: true, coverage, force: true });
    if (!r.ok || !r.body || !r.body.map) {
      setLoadError(r.message || "Couldn't read the shop map.");
      return;
    }
    setLoadError(null);
    setBody(r.body);
    setDraft(draftFrom(r.body));
    setMode(null);
  }, []);

  const loadCalib = useCallback(async () => {
    const r = await loadCalibration();
    if (!mounted.current) return;
    if (r.ok && r.body) setCalib(r.body);
    setCalibReads((n) => n + 1);
  }, []);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // A point just started has no scan yet (the beacon scans on its Find, the gateway reports it a
  // little later): read the calibration again every CALIB_POLL_MS while any open point has no
  // readings, for at most CALIB_POLL_FOR_MS (a new point starts the wait again).
  useEffect(() => {
    if (!enabled || !calib) return undefined;
    if (!calibrationAwaiting(calib)) {
      calibPollFrom.current = null;
      return undefined;
    }
    if (calibPollFrom.current === null) calibPollFrom.current = Date.now();
    if (Date.now() - calibPollFrom.current >= CALIB_POLL_FOR_MS) return undefined;
    const t = setTimeout(loadCalib, CALIB_POLL_MS);
    return () => clearTimeout(t);
  }, [enabled, calib, calibReads, loadCalib]);

  useEffect(() => {
    if (!enabled) return;
    load();
    loadCalib();
  }, [enabled, load, loadCalib]);

  useEffect(() => {
    if (!enabled || !showPallets) return undefined;
    let live = true;
    loadBeacons().then((r) => { if (live && r.ok && r.body) setPallets(r.body.beacons || []); });
    return () => { live = false; };
  }, [enabled, showPallets]);

  // Coverage comes with the map read (?coverage=1): ask for it once it is switched on.
  const toggleCoverage = async () => {
    const next = !showCoverage;
    setShowCoverage(next);
    if (next && body && !body.coverage) {
      const r = await loadShopMap(null, { diagnostics: true, coverage: true, force: true });
      if (r.ok && r.body) setBody((b) => ({ ...b, coverage: r.body.coverage }));
    }
  };

  const geo = useMemo(() => (draft ? parseGeometryText(draft.geometryText) : null), [draft]);
  const payload = useMemo(() => {
    if (!body || !draft) return null;
    return shopMapPayload(body, {
      name: draft.name,
      geometry: geo && geo.ok ? geo.geometry : body.map.geometry,
      anchors: draft.anchors, gateways: draft.gateways, zones: draft.zones,
      settings: draft.zoneRule !== zoneRuleOf(body.map.settings)
        ? settingsWithZoneRule(body.map.settings, draft.zoneRule) : undefined,
    }, null);
  }, [body, draft, geo]);
  const dirty = payloadHasChanges(payload) || !!(geo && !geo.ok);

  if (!loaded) return <div className="container mt-4 text-muted">Loading…</div>;
  if (!enabled) {
    return <div className="container mt-4"><div className="alert alert-secondary">The shop map isn't switched on here yet.</div></div>;
  }
  if (!body || !draft) {
    return (
      <div className="container mt-4">
        {loadError ? <div className="alert alert-danger">{loadError}</div> : <div className="text-muted">Loading the shop map…</div>}
      </div>
    );
  }

  const setAnchor = (no, patch) => setDraft((d) => ({
    ...d, anchors: d.anchors.map((a) => (a.anchor_no === no ? { ...a, ...patch } : a)),
  }));
  const setGateway = (id, patch) => setDraft((d) => ({
    ...d, gateways: d.gateways.map((g) => (g.gateway_id === id ? { ...g, ...patch } : g)),
  }));
  const setZone = (key, patch) => setDraft((d) => ({ ...d, zones: d.zones.map((z) => (z._key === key ? { ...z, ...patch } : z)) }));

  const onHandleMove = (key, x, y) => {
    const [kind, id] = key.split(/:(.+)/);
    if (kind === 'anchor') setAnchor(Number(id), { x_ft: x, y_ft: y, placed: true });
    if (kind === 'gateway') setGateway(id, { x_ft: x, y_ft: y });
  };

  const addDrawPoint = (x, y) => setDrawing((d) => ({ ...d, pts: [...d.pts, [snapFt(x), snapFt(y)]] }));

  const onMapClick = (x, y) => {
    if (!mode) return;
    if (mode.kind === 'place') {
      setAnchor(mode.anchorNo, { x_ft: x, y_ft: y, placed: true });
      setMode(null);
    } else if (mode.kind === 'draw') {
      addDrawPoint(x, y);
    } else if (mode.kind === 'calib') {
      setCalibForm((f) => ({ ...f, x: String(x), y: String(y) }));
      setMode(null);
    }
  };

  const finishZone = () => {
    const name = drawing.name.trim();
    if (drawing.pts.length < 3 || !name) return;
    setDraft((d) => ({
      ...d,
      zones: [...d.zones, { _key: `new${Date.now()}`, kind: drawing.kind, name, polygon: drawing.pts, sort: 0, is_active: true }],
    }));
    setDrawing({ kind: drawing.kind, name: '', pts: [], x: '', y: '' });
    setMode(null);
  };

  const save = async () => {
    setMsg(null);
    if (geo && !geo.ok) { setMsg({ tone: 'danger', text: `Fix the geometry first: ${geo.error}` }); return; }
    const names = draft.zones.filter((z) => z.is_active !== false).map((z) => `${z.kind}:${String(z.name || '').trim().toLowerCase()}`);
    if (names.some((n) => n.endsWith(':'))) { setMsg({ tone: 'danger', text: 'Every zone needs a name.' }); return; }
    if (new Set(names).size !== names.length) { setMsg({ tone: 'danger', text: 'Two zones of the same kind have the same name.' }); return; }
    const pin = await pinPrompt('Admin PIN to save the shop map', { face: false });
    if (!pin) return;
    setSaving(true);
    try {
      const r = await saveShopMap({ ...payload, pin });
      if (r.ok) {
        setMsg({ tone: 'success', text: 'Shop map saved. Locations are worked out again on the new map.' });
        await load({ coverage: showCoverage });
      } else if (r.stale) {
        setMsg({ tone: 'warning', text: 'Someone else saved the map. Reload.', reload: true });
      } else {
        setMsg({ tone: 'danger', text: r.message });
      }
    } finally {
      setSaving(false);
    }
  };

  const reload = async () => {
    // eslint-disable-next-line no-alert
    if (dirty && !window.confirm('Drop your changes and read the map again?')) return;
    setMsg(null);
    await load({ coverage: showCoverage });
  };

  const refit = async () => {
    const pin = await pinPrompt('Admin PIN to refit the radio model', { face: false });
    if (!pin) return;
    const r = await refitShopModel(pin);
    if (!r.ok) { setMsg({ tone: 'danger', text: r.message }); return; }
    setMsg({ tone: 'success', text: body?.model?.source === 'calibration'
      ? 'Gateway fit refreshed. The estimates keep using the walk calibration.'
      : 'Model refitted.' });
    // keep the layout edits: only the model (and its diagnostics) are read again
    const m = await loadShopMap(null, { diagnostics: true, coverage: showCoverage, force: true });
    if (m.ok && m.body) setBody((b) => ({ ...b, model: m.body.model, diagnostics: m.body.diagnostics, coverage: m.body.coverage }));
  };

  const startCalibration = async (e) => {
    e.preventDefault();
    const x = toNum(calibForm.x);
    const y = toNum(calibForm.y);
    const beaconId = calibForm.beacon_id.trim().toUpperCase();
    if (!beaconId || x === null || y === null) { setMsg({ tone: 'danger', text: 'A calibration point needs a beacon and its x / y.' }); return; }
    const pin = await pinPrompt('Admin PIN to start a calibration point', { face: false });
    if (!pin) return;
    const r = await addCalibrationPoint({
      beacon_id: beaconId, x_ft: x, y_ft: y, z_ft: toNum(calibForm.z) ?? 3, notes: calibForm.notes.trim() || null, pin,
    });
    if (!r.ok) { setMsg({ tone: 'danger', text: r.message }); return; }
    setMsg({ tone: 'success', text: `${beaconId} is a calibration point at (${x}, ${y}). It flashes and scans now.` });
    setCalibForm((f) => ({ ...f, notes: '' }));
    calibPollFrom.current = null;
    loadCalib();
  };

  const endCalibration = async (p) => {
    const pin = await pinPrompt(`Admin PIN to end calibration point ${p.point_id}`, { face: false });
    if (!pin) return;
    const r = await patchCalibrationPoint(p.point_id, { end: true, pin });
    if (!r.ok) { setMsg({ tone: 'danger', text: r.message }); return; }
    loadCalib();
  };

  const model = body.model || {};
  // jobq-api (2026-10-21): source 'calibration' = the estimates use the walk calibration; gateway_fit is
  // the hourly gateway <-> anchor fit, kept for the diagnostics only
  const calibrated = model.source === 'calibration';
  const cal = model.calibration || {};
  const gw = model.gateway_fit || null;
  const residuals = residualRows(model, body.diagnostics);
  const unplaced = heardNotPlaced(draft.anchors);
  // a tag belongs to a SAVED, switched-on pallet zone (a zone drawn in this draft has no id until it is saved)
  const tagZones = draft.zones.filter((z) => z.kind === 'pallet' && z.is_active !== false && z.zone_id);
  const zoneName = (id) => {
    const z = draft.zones.find((q) => q.zone_id === id);
    return z ? z.name : `zone ${id}`;
  };
  const untaggedZones = tagZones.filter((z) => !draft.anchors.some((a) => a.is_active !== false && a.zone_id === z.zone_id));
  const previewGeometry = geo && geo.ok ? geo.geometry : body.map.geometry;
  const handles = [
    ...draft.anchors.filter((a) => a.x_ft !== null && a.y_ft !== null && a.x_ft !== undefined)
      .map((a) => ({ key: `anchor:${a.anchor_no}`, x: a.x_ft, y: a.y_ft, label: `anchor ${a.anchor_no}` })),
    ...draft.gateways.filter((g) => g.x_ft !== null && g.y_ft !== null && g.x_ft !== undefined)
      .map((g) => ({ key: `gateway:${g.gateway_id}`, x: g.x_ft, y: g.y_ft, label: `gateway ${g.gateway_id}` })),
  ];
  const calibPoints = calib && Array.isArray(calib.points) ? calib.points : [];
  const markers = [
    ...(showPallets ? pallets.filter((b) => hasPoint(b.location)).map((b) => ({
      key: b.beacon_id, x: Number(b.location.x_ft), y: Number(b.location.y_ft),
      label: b.lot ? `TRK ${b.lot.trk}` : b.beacon_id, color: b.location.rough ? '#fd7e14' : '#0d6efd',
      title: `${b.beacon_id}${b.lot ? ` · TRK ${b.lot.trk}` : ''}${b.location.label ? ` · ${b.location.label}` : ''}`,
    })) : []),
    ...calibPoints.filter((p) => Number.isFinite(Number(p.x_ft))).map((p) => ({
      key: `cal-${p.point_id}`, x: Number(p.x_ft), y: Number(p.y_ft), shape: 'cross', color: '#198754',
      title: `Calibration ${p.point_id}: ${p.beacon_id}`,
    })),
    ...calibPoints.map((p) => ({ p, row: calibrationRow(p) })).filter(({ row }) => row.est).map(({ p, row }) => ({
      key: `cal-est-${p.point_id}`, x: row.est.x, y: row.est.y, color: '#198754',
      title: `Latest estimate for calibration ${p.point_id}`,
    })),
  ];
  const modeNote = mode && ({
    place: `Click the map where anchor ${mode.anchorNo} is mounted.`,
    draw: `Click the map to add the zone's corners (${drawing.pts.length} so far).`,
    calib: 'Click the map where the beacon is standing.',
  })[mode.kind];
  const summary = calib && calib.summary ? calib.summary : null;

  return (
    <div className="container-fluid mt-3" style={styles.page}>
      <div style={styles.head}>
        <h3 className="mb-0"><i className="bi bi-map me-2"></i>Shop map</h3>
        <span className="badge bg-secondary">rev {body.map.rev}</span>
        {body.map.updated_by && (
          <span className="small text-muted">saved by {body.map.updated_by} {lastSeenLabel(body.map.updated_at)}</span>
        )}
        <div className="ms-auto d-flex flex-wrap gap-2">
          <Link to="/beacons" className="btn btn-sm btn-outline-secondary">Beacons</Link>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={reload}>Reload</button>
          <button type="button" className="btn btn-sm btn-primary" onClick={save} disabled={saving || !dirty}>
            {saving ? 'Saving…' : 'Save map'}
          </button>
        </div>
      </div>
      {dirty && <div className="small text-warning mb-2" data-testid="shopmap-unsaved">Unsaved changes</div>}
      {msg && (
        <div className={`alert alert-${msg.tone} py-2 d-flex align-items-center gap-2`} role="status">
          <span>{msg.text}</span>
          {msg.reload && <button type="button" className="btn btn-sm btn-outline-secondary ms-auto" onClick={() => load({ coverage: showCoverage })}>Reload</button>}
        </div>
      )}

      <div className="row g-3">
        <div className="col-lg-7">
          <div style={styles.toolbar}>
            <div className="form-check form-switch mb-0">
              <input className="form-check-input" type="checkbox" id="sm-coverage" checked={showCoverage} onChange={toggleCoverage} />
              <label className="form-check-label small" htmlFor="sm-coverage">Coverage</label>
            </div>
            <div className="form-check form-switch mb-0">
              <input className="form-check-input" type="checkbox" id="sm-pallets" checked={showPallets}
                onChange={() => setShowPallets((v) => !v)} />
              <label className="form-check-label small" htmlFor="sm-pallets">All pallets</label>
            </div>
            {showCoverage && (
              <span style={styles.legend}>Green: 3+ anchors heard · orange: 1–2 · red: none (cells under −88 dBm)</span>
            )}
          </div>
          {modeNote && (
            <div className="alert alert-info py-1 px-2 d-flex align-items-center gap-2" style={styles.modeNote} data-testid="shopmap-mode">
              <span>{modeNote}</span>
              <button type="button" className="btn btn-sm btn-link ms-auto p-0" onClick={() => setMode(null)}>Cancel</button>
            </div>
          )}
          <ShopMap
            map={body}
            geometry={previewGeometry}
            anchors={draft.anchors}
            gateways={draft.gateways}
            zones={draft.zones}
            coverage={showCoverage ? body.coverage : null}
            markers={markers}
            handles={handles}
            onHandleMove={onHandleMove}
            onMapClick={mode ? onMapClick : null}
            drawing={mode && mode.kind === 'draw' ? drawing.pts : null}
            selectedZoneId={selectedZone}
            title="Shop map editor"
          />
          <div style={styles.legend} className="mt-1">
            Drag a numbered anchor (purple) or a gateway (green) to move it; arrow keys move a selected one 0.5 ft (Shift: 5 ft).
          </div>

          <div className="card mt-3">
            <div className="card-header d-flex align-items-center gap-2">
              <strong>Geometry</strong>
              <span className="small text-muted">walls, doors, racks, machines (feet; x east, y north)</span>
            </div>
            <div className="card-body">
              <textarea className="form-control" style={styles.textarea} aria-label="Geometry JSON" value={draft.geometryText}
                onChange={(e) => setDraft((d) => ({ ...d, geometryText: e.target.value }))} />
              {geo && !geo.ok
                ? <div className="small text-danger mt-1" data-testid="geometry-error">{geo.error}</div>
                : <div className="small text-muted mt-1">The map above previews this geometry.</div>}
            </div>
          </div>
        </div>

        <div className="col-lg-5">
          <div className="card mb-3">
            <div className="card-header"><strong>Anchors</strong> <span className="small text-muted">(fixed BLE tags on the walls)</span></div>
            <div className="card-body pb-1" data-testid="zone-rule">
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <label className="small fw-semibold mb-0" htmlFor="zone-rule-select">A Find answers with</label>
                <select id="zone-rule-select" className="form-select form-select-sm" style={{ width: 170 }}
                  value={draft.zoneRule} onChange={(e) => setDraft((d) => ({ ...d, zoneRule: e.target.value }))}>
                  {ZONE_RULES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div className="small text-muted mt-1">
                {(ZONE_RULES.find((r) => r.value === draft.zoneRule) || ZONE_RULES[1]).help}
                {draft.zoneRule === 'loudest_tag' && untaggedZones.length > 0
                  ? `. No tag in: ${untaggedZones.map((z) => z.name).join(', ')}` : ''}
              </div>
            </div>
            {unplaced.length > 0 && (
              <div className="card-body pb-0" data-testid="unplaced-anchors">
                <div className="small fw-semibold mb-1">Heard but not placed</div>
                {unplaced.map((a) => (
                  <div key={a.anchor_no} className="d-flex align-items-center gap-2 small mb-1">
                    <span className="badge bg-secondary">#{a.anchor_no}</span>
                    <span className="text-muted">
                      heard by {(a.heard_by || []).map((h) => `${h.gateway_id} (${h.rssi} dBm)`).join(', ') || a.last_heard_gateway || 'a gateway'}
                      {a.last_heard_at ? ` · ${lastSeenLabel(a.last_heard_at)}` : ''}
                    </span>
                    <button type="button" className="btn btn-sm btn-outline-primary ms-auto py-0"
                      onClick={() => setMode({ kind: 'place', anchorNo: a.anchor_no })}>Place on map</button>
                  </div>
                ))}
              </div>
            )}
            <div className="table-responsive" style={styles.tableWrap}>
              <table className="table table-sm align-middle mb-0">
                <thead><tr><th>#</th><th>Name</th><th>x</th><th>y</th><th>z</th><th>Zone</th><th>On</th><th>Heard</th></tr></thead>
                <tbody>
                  {draft.anchors.map((a) => (
                    <tr key={a.anchor_no} data-testid={`anchor-row-${a.anchor_no}`}>
                      <td className="fw-bold">{a.anchor_no}</td>
                      <td>
                        <input className="form-control form-control-sm" style={{ minWidth: 90 }} aria-label={`Anchor ${a.anchor_no} name`}
                          value={a.name || ''} onChange={(e) => setAnchor(a.anchor_no, { name: e.target.value || null })} />
                      </td>
                      <td><NumCell label={`Anchor ${a.anchor_no} x`} value={a.x_ft} onChange={(v) => setAnchor(a.anchor_no, { x_ft: v, placed: v !== null })} /></td>
                      <td><NumCell label={`Anchor ${a.anchor_no} y`} value={a.y_ft} onChange={(v) => setAnchor(a.anchor_no, { y_ft: v })} /></td>
                      <td><NumCell label={`Anchor ${a.anchor_no} z`} value={a.z_ft} style={styles.smallNum} onChange={(v) => setAnchor(a.anchor_no, { z_ft: v })} /></td>
                      <td>
                        <select className="form-select form-select-sm" style={styles.zoneSel} aria-label={`Anchor ${a.anchor_no} zone`}
                          value={a.zone_id === null || a.zone_id === undefined ? '' : String(a.zone_id)}
                          onChange={(e) => setAnchor(a.anchor_no, { zone_id: e.target.value === '' ? null : Number(e.target.value) })}>
                          <option value="">—</option>
                          {tagZones.map((z) => <option key={z.zone_id} value={String(z.zone_id)}>{z.name}</option>)}
                          {a.zone_id !== null && a.zone_id !== undefined && !tagZones.some((z) => z.zone_id === a.zone_id) && (
                            <option value={String(a.zone_id)}>{`${zoneName(a.zone_id)} (not an active pallet zone)`}</option>
                          )}
                        </select>
                      </td>
                      <td>
                        <input type="checkbox" className="form-check-input" aria-label={`Anchor ${a.anchor_no} active`}
                          checked={a.is_active !== false} onChange={(e) => setAnchor(a.anchor_no, { is_active: e.target.checked })} />
                      </td>
                      <td className="small text-muted text-nowrap">{a.last_heard_at ? lastSeenLabel(a.last_heard_at) : 'never'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card mb-3">
            <div className="card-header"><strong>Gateways</strong> <span className="small text-muted">(the shop Pis)</span></div>
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead><tr><th>Gateway</th><th>x</th><th>y</th><th>z</th><th>Last sync</th></tr></thead>
                <tbody>
                  {draft.gateways.map((g) => (
                    <tr key={g.gateway_id} data-testid={`gateway-row-${g.gateway_id}`}>
                      <td style={styles.mono}>{g.gateway_id}{g.station ? <span className="small text-muted"> · dock {g.station}</span> : null}</td>
                      <td><NumCell label={`Gateway ${g.gateway_id} x`} value={g.x_ft} onChange={(v) => setGateway(g.gateway_id, { x_ft: v })} /></td>
                      <td><NumCell label={`Gateway ${g.gateway_id} y`} value={g.y_ft} onChange={(v) => setGateway(g.gateway_id, { y_ft: v })} /></td>
                      <td><NumCell label={`Gateway ${g.gateway_id} z`} value={g.z_ft} style={styles.smallNum} onChange={(v) => setGateway(g.gateway_id, { z_ft: v })} /></td>
                      <td className="small text-muted text-nowrap">{lastSeenLabel(g.last_seen_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card mb-3">
            <div className="card-header d-flex align-items-center">
              <strong>Zones</strong>
              <span className="small text-muted ms-2">pallet zones (what a Find answers), areas (the floor) and spots</span>
            </div>
            <div className="table-responsive" style={styles.tableWrap}>
              <table className="table table-sm align-middle mb-0">
                <thead><tr><th>Kind</th><th>Name</th><th>On</th><th></th></tr></thead>
                <tbody>
                  {draft.zones.map((z) => (
                    <tr key={z._key} className={z.is_active === false ? 'text-muted' : ''} data-testid={`zone-row-${z.name}`}>
                      <td className="small">{z.kind === 'pallet' ? 'pallet zone' : z.kind}</td>
                      <td>
                        <input className="form-control form-control-sm" aria-label={`Zone ${z._key} name`} value={z.name || ''}
                          onChange={(e) => setZone(z._key, { name: e.target.value })} />
                      </td>
                      <td>
                        <input type="checkbox" className="form-check-input" aria-label={`Zone ${z.name} active`}
                          checked={z.is_active !== false} onChange={(e) => setZone(z._key, { is_active: e.target.checked })} />
                      </td>
                      <td>
                        {z.zone_id && (
                          <button type="button" className="btn btn-sm btn-link p-0"
                            onClick={() => setSelectedZone((s) => (s === z.zone_id ? null : z.zone_id))}>
                            {selectedZone === z.zone_id ? 'Hide' : 'Show'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="card-body border-top">
              {mode && mode.kind === 'draw' ? (
                <div data-testid="zone-draw">
                  <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
                    <select className="form-select form-select-sm" style={{ width: 130 }} aria-label="New zone kind" value={drawing.kind}
                      onChange={(e) => setDrawing((d) => ({ ...d, kind: e.target.value }))}>
                      <option value="pallet">pallet zone</option>
                      <option value="spot">spot</option>
                      <option value="area">area</option>
                    </select>
                    <input className="form-control form-control-sm" style={{ width: 160 }} placeholder="Zone name" aria-label="New zone name"
                      value={drawing.name} onChange={(e) => setDrawing((d) => ({ ...d, name: e.target.value }))} />
                  </div>
                  <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
                    <span className="small">or type a corner:</span>
                    <input type="number" step="0.5" className="form-control form-control-sm" style={styles.num} aria-label="Corner x"
                      value={drawing.x} onChange={(e) => setDrawing((d) => ({ ...d, x: e.target.value }))} />
                    <input type="number" step="0.5" className="form-control form-control-sm" style={styles.num} aria-label="Corner y"
                      value={drawing.y} onChange={(e) => setDrawing((d) => ({ ...d, y: e.target.value }))} />
                    <button type="button" className="btn btn-sm btn-outline-secondary"
                      disabled={toNum(drawing.x) === null || toNum(drawing.y) === null}
                      onClick={() => { addDrawPoint(toNum(drawing.x), toNum(drawing.y)); setDrawing((d) => ({ ...d, x: '', y: '' })); }}>
                      Add corner
                    </button>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    <button type="button" className="btn btn-sm btn-primary" onClick={finishZone}
                      disabled={drawing.pts.length < 3 || !drawing.name.trim()}>Add zone ({drawing.pts.length} corners)</button>
                    <button type="button" className="btn btn-sm btn-outline-secondary" disabled={!drawing.pts.length}
                      onClick={() => setDrawing((d) => ({ ...d, pts: d.pts.slice(0, -1) }))}>Undo corner</button>
                    <button type="button" className="btn btn-sm btn-link" onClick={() => { setMode(null); setDrawing((d) => ({ ...d, pts: [] })); }}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setMode({ kind: 'draw' })}>Draw a zone</button>
              )}
              <div className="small text-muted mt-2">A zone switched off stays in JobQ (old locations keep their names); it is never deleted.</div>
            </div>
          </div>

          <div className="card mb-3" data-testid="model-panel">
            <div className="card-header d-flex align-items-center">
              <strong>Radio model</strong>
              <span className="badge bg-secondary ms-2">rev {model.model_rev ?? '—'}</span>
              {calibrated
                ? <span className="badge bg-info text-dark ms-2" data-testid="model-source">walk calibration</span>
                : model.fitted
                  ? <span className="badge bg-success ms-2">fitted</span>
                  : <span className="badge bg-warning text-dark ms-2">defaults</span>}
              <button type="button" className="btn btn-sm btn-outline-primary ms-auto" onClick={refit}>Refit</button>
            </div>
            <div className="card-body">
              <div className="small">
                P0 {fmt(model.p0_dbm)} dBm at 1 m · n {fmt(model.n, 2)} · σ {fmt(model.sigma_db)} dB · wall {fmt(model.wall_db)} dB
                {Number.isFinite(Number(model.rms_db)) ? ` · RMS ${fmt(model.rms_db)} dB` : ''}
                {model.pairs !== undefined && !Array.isArray(model.pairs) ? ` · ${model.pairs} pairs` : ''}
              </div>
              {calibrated && (
                <div className="small text-muted" data-testid="model-calibration">
                  Walk calibration {cal.calibrated_on || ''}
                  {cal.spots ? ` · ${cal.spots} spots, ${cal.readings ?? '?'} readings` : ''}
                  {Number.isFinite(Number(cal.loo_median_ft)) ? ` · tested median ${fmt(cal.loo_median_ft)} ft` : ''}
                  {cal.loo_right_area ? `, right area ${cal.loo_right_area}` : ''}
                </div>
              )}
              {calibrated && gw && (
                <div className="small text-muted" data-testid="model-gateway-fit">
                  Gateway fit (not used for the estimates): P0 {fmt(gw.p0_dbm)} dBm · n {fmt(gw.n, 2)} · σ {fmt(gw.sigma_db)} dB · wall {fmt(gw.wall_db)} dB
                </div>
              )}
              {model.fitted_at && (
                <div className="small text-muted">{calibrated ? 'Gateway fit ' : 'Fitted '}{shopClock(model.fitted_at)}</div>
              )}
              {!model.fitted && model.reason && <div className="small text-warning">{model.reason}</div>}
              {model.anchor_offsets && Object.keys(model.anchor_offsets).length > 0 && (
                <div className="small text-muted">
                  Anchor offsets: {Object.entries(model.anchor_offsets).map(([no, db]) => `#${no} ${Number(db) > 0 ? '+' : ''}${fmt(db)} dB`).join(', ')}
                </div>
              )}
              {residuals.length > 0 && (
                <div className="small fw-semibold mt-2" data-testid="residuals-caption">
                  Live fit: how each gateway hears each anchor now, against a model fitted to those links right now
                  (not saved; Refit saves it)
                </div>
              )}
              {residuals.length > 0 && (
                <div className="table-responsive mt-1" style={styles.tableWrap}>
                  <table className="table table-sm mb-0">
                    <thead><tr><th>Gateway</th><th>Anchor</th><th>Heard</th><th>Model</th><th>Off by</th></tr></thead>
                    <tbody>
                      {residuals.map((r) => (
                        <tr key={r.key} data-testid="residual-row" data-flagged={r.flagged ? '1' : '0'}>
                          <td style={styles.mono}>{r.gateway_id}</td>
                          <td>#{r.anchor_no}</td>
                          <td>{fmt(r.rssi, 0)}</td>
                          <td>{fmt(r.pred, 0)}</td>
                          <td style={r.flagged ? styles.flagged : undefined}>
                            {fmt(r.resid)} dB{r.flagged ? ' ⚠ over 2σ: check where this anchor is' : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div className="card mb-3" data-testid="calibration-panel">
            <div className="card-header"><strong>Calibration walk</strong></div>
            <div className="card-body">
              <form className="d-flex flex-wrap gap-2 align-items-end" onSubmit={startCalibration}>
                <div>
                  <label className="form-label small mb-0" htmlFor="cal-beacon">Beacon</label>
                  <input id="cal-beacon" className="form-control form-control-sm" style={{ width: 100 }} placeholder="BCN-003"
                    value={calibForm.beacon_id} onChange={(e) => setCalibForm((f) => ({ ...f, beacon_id: e.target.value }))} />
                </div>
                <div>
                  <label className="form-label small mb-0" htmlFor="cal-x">x</label>
                  <input id="cal-x" type="number" step="0.5" className="form-control form-control-sm" style={styles.num}
                    value={calibForm.x} onChange={(e) => setCalibForm((f) => ({ ...f, x: e.target.value }))} />
                </div>
                <div>
                  <label className="form-label small mb-0" htmlFor="cal-y">y</label>
                  <input id="cal-y" type="number" step="0.5" className="form-control form-control-sm" style={styles.num}
                    value={calibForm.y} onChange={(e) => setCalibForm((f) => ({ ...f, y: e.target.value }))} />
                </div>
                <div>
                  <label className="form-label small mb-0" htmlFor="cal-z">z</label>
                  <input id="cal-z" type="number" step="0.5" className="form-control form-control-sm" style={styles.smallNum}
                    value={calibForm.z} onChange={(e) => setCalibForm((f) => ({ ...f, z: e.target.value }))} />
                </div>
                <div style={{ flex: '1 1 120px' }}>
                  <label className="form-label small mb-0" htmlFor="cal-notes">Notes</label>
                  <input id="cal-notes" className="form-control form-control-sm" value={calibForm.notes}
                    onChange={(e) => setCalibForm((f) => ({ ...f, notes: e.target.value }))} />
                </div>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setMode({ kind: 'calib' })}>Pick on map</button>
                <button type="submit" className="btn btn-sm btn-primary">Start point</button>
              </form>
              <div className="small text-muted mt-1">
                Stand the beacon at the spot, then Start: it flashes and scans, and the estimate is compared with where it really is.
              </div>
              {summary && (
                <div className="small mt-2" data-testid="calibration-summary">
                  {summary.points !== undefined && <span className="me-3">{summary.points} points</span>}
                  {summary.median_err_ft !== undefined && <span className="me-3">median error {fmt(summary.median_err_ft)} ft</span>}
                  {summary.inside_r80_pct !== undefined && <span className="me-3">inside r80 {fmt(summary.inside_r80_pct, 0)}%</span>}
                  {summary.area_ok_pct !== undefined && <span>right area {fmt(summary.area_ok_pct, 0)}%</span>}
                </div>
              )}
              {calibPoints.length > 0 && (
                <div className="table-responsive mt-2" style={styles.tableWrap}>
                  <table className="table table-sm align-middle mb-0">
                    <thead><tr><th>#</th><th>Beacon</th><th>At</th><th title="Median over its scans">Error</th><th>In r80</th><th>Area</th><th></th></tr></thead>
                    <tbody>
                      {calibPoints.map((p) => {
                        const row = calibrationRow(p);
                        return (
                        <tr key={p.point_id} data-testid={`calibration-row-${p.point_id}`}>
                          <td>{p.point_id}</td>
                          <td style={styles.mono}>{p.beacon_id}</td>
                          <td className="small text-nowrap">({fmt(p.x_ft)}, {fmt(p.y_ft)}){row.truthArea ? ` · ${row.truthArea}` : ''}</td>
                          <td>{row.errFt === null ? (p.ended_at ? '—' : '…') : `${fmt(row.errFt)} ft`}</td>
                          <td>{row.insideR80 === true ? 'yes' : row.insideR80 === false ? 'no' : '—'}</td>
                          <td>{row.areaOk === true ? 'right' : row.areaOk === false ? 'wrong' : '—'}</td>
                          <td className="text-end">
                            {p.ended_at
                              ? <span className="small text-muted">ended {shopClock(p.ended_at)}</span>
                              : <button type="button" className="btn btn-sm btn-outline-secondary py-0" onClick={() => endCalibration(p)}>End</button>}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
          <div className="small text-muted mb-3">
            Signed in as {currentActor() || 'an admin'}. Anchors: iBeacon {body.map.anchor_uuid || '—'}, major {body.map.anchor_major ?? '—'}.
          </div>
        </div>
      </div>
    </div>
  );
}
